import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import { useUnderwriterContext } from '../../views/underwriter/provider/UnderwriterProvider';
import { useCowbellTranslations } from '../../../../../../i18n/translations';
import { useUiSettings } from '../../providers/UiSettingsProvider';
import { isUnderwriterContext } from '../../views/underwriter/types';
import CarrierSelect from './carrier-select/CarrierSelect';
import StagedWas from '../../views/underwriter/draft/components/StagedWas';
import { useStagedSx } from '../../views/underwriter/draft/components/useStagedSx';
import StagedRowActions from '../../views/underwriter/draft/components/StagedRowActions';
import {
  globalFieldPath,
  raterOptionPath,
} from '../../views/underwriter/draft/stagedFields';
import GlobalInput from '../GlobalInput';
import GlobalsRow from './GlobalsRow';
import GlobalRetroActiveInput from '../GlobalRetroActiveInput';
import { BOUND } from '../../../../../_statics/binder.statics';
import { useFeature } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_KEYS } from '../../../../../../config/growthbook';
import {
  UW_BORDERLESS_SELECT_SX,
  UW_GLOBAL_LABEL_STYLE,
} from '../../views/underwriter/components/uwTileSpacing';

interface StandardGlobalsProps {
  /** Product version, or the related-quotes list; sits before Carrier. */
  readonly leading?: React.ReactNode;
}

/**
 * The single-rater globals: product version and Carrier on the left, the rest
 * packed right, as on the multi-rater row.
 */
const StandardGlobals = ({ leading }: StandardGlobalsProps) => {
  const context = useUnderwriterContext();
  const { t } = useCowbellTranslations('common', 'coverage');
  const { uiSettings } = useUiSettings();
  const isUwQuoteState = isUnderwriterContext(context);
  const globalProps = context.state.global?.value?.props || {};
  const globalRules = context.state.global?.rules || {};
  const restrictUwCarrierChangeFeature = useFeature(
    FEATURE_FLAG_KEYS.RESTRICT_UW_CARRIER_CHANGE
  );
  // Carrier selection should be disabled for bound quotes (PRD-711)
  const isBound =
    isUwQuoteState && context.state.quote?.quote?.agencyStatus === BOUND;

  const carrierSx = useStagedSx([raterOptionPath('carrier')]);
  const limitSx = useStagedSx([globalFieldPath('limit')]);
  const retentionSx = useStagedSx([globalFieldPath('deductible')]);
  const waitingSx = useStagedSx([globalFieldPath('waitingPeriod')]);
  const retroSx = useStagedSx([globalFieldPath('retroactive')]);

  const carrierField = isUwQuoteState ? (
    <GlobalField sx={carrierSx}>
      <CarrierSelect
        labelStyle={UW_GLOBAL_LABEL_STYLE}
        value={context.state.raterOptions.carrier.value}
        options={context.state.raterOptions.carrier.options}
        onSelect={(treatyId) =>
          context.handlers.setRaterOption({
            id: 'carrier',
            value: treatyId,
          })
        }
        disabled={isBound && restrictUwCarrierChangeFeature.on}
        labelAdornment={
          <StagedRowActions paths={[raterOptionPath('carrier')]} />
        }
      />
      <StagedWas paths={[raterOptionPath('carrier')]} prefixed />
    </GlobalField>
  ) : null;

  const limitField = (
    <GlobalField sx={limitSx}>
      <GlobalInput
        label="Aggregate Limit"
        name="limit"
        setGlobalValue={context.handlers.setGlobalValue}
        selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
        labelStyle={UW_GLOBAL_LABEL_STYLE}
        value={Number(globalProps.limit?.value) || 0}
        options={globalRules.limit?.options ?? []}
        labelAdornment={<StagedRowActions paths={[globalFieldPath('limit')]} />}
      />
      <StagedWas paths={[globalFieldPath('limit')]} prefixed />
    </GlobalField>
  );

  const retentionField = (
    <GlobalField sx={retentionSx}>
      <GlobalInput
        label={t('retention')}
        name="deductible"
        setGlobalValue={context.handlers.setGlobalValue}
        selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
        labelStyle={UW_GLOBAL_LABEL_STYLE}
        value={Number(globalProps.deductible?.value) || 0}
        options={globalRules.deductible?.options ?? []}
        labelAdornment={
          <StagedRowActions paths={[globalFieldPath('deductible')]} />
        }
      />
      <StagedWas paths={[globalFieldPath('deductible')]} prefixed />
    </GlobalField>
  );

  const waitingPeriodField = uiSettings.showGlobalWaitingPeriod ? (
    <GlobalField sx={waitingSx}>
      <GlobalInput
        label={t('coverage:waitingPeriod')}
        name="waitingPeriod"
        setGlobalValue={context.handlers.setGlobalValue}
        selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
        labelStyle={UW_GLOBAL_LABEL_STYLE}
        value={Number(globalProps.waitingPeriod?.value) || 0}
        options={globalRules.waitingPeriod?.options ?? []}
        labelAdornment={
          <StagedRowActions paths={[globalFieldPath('waitingPeriod')]} />
        }
      />
      <StagedWas paths={[globalFieldPath('waitingPeriod')]} prefixed />
    </GlobalField>
  ) : null;

  const retroactiveField = (
    <GlobalField sx={retroSx}>
      <GlobalInput
        label="Retroactive Date"
        name="retroactive"
        setGlobalValue={context.handlers.setGlobalValue}
        selectProps={{ sx: UW_BORDERLESS_SELECT_SX }}
        labelStyle={UW_GLOBAL_LABEL_STYLE}
        value={Number(globalProps.retroactive?.props?.period?.value) || 0}
        options={globalRules.retroactive?.retroPeriods ?? []}
        labelAdornment={
          <StagedRowActions paths={[globalFieldPath('retroactive')]} />
        }
      >
        {globalRules.retroactive?.isCustomDateAllowed && (
          <GlobalRetroActiveInput
            setGlobalValue={context.handlers.setGlobalValue}
            retroValueProps={globalProps.retroactive || {}}
            options={globalRules.retroactive?.retroPeriods ?? []}
            maxDate={globalRules.retroactive?.to as string}
            defaultDate={globalRules.retroactive?.customDateDefault as string}
          />
        )}
      </GlobalInput>
      <StagedWas paths={[globalFieldPath('retroactive')]} prefixed />
    </GlobalField>
  );

  return (
    <GlobalsRow
      leading={
        <>
          {leading}
          {carrierField}
        </>
      }
    >
      {limitField}
      {retentionField}
      {waitingPeriodField}
      {retroactiveField}
    </GlobalsRow>
  );
};

/** One global's control; shrinks on a narrow window. */
const GlobalField = ({
  children,
  sx,
}: Readonly<{ children: React.ReactNode; sx?: SxProps<Theme> }>) => (
  <Box flex={GLOBAL_FIELD_FLEX} minWidth={0} sx={sx}>
    {children}
  </Box>
);

const GLOBAL_FIELD_FLEX = '0 1 12rem';

export default StandardGlobals;
